import { css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { LitPage } from '../lit-page';

/**
 * Centred card shell for the auth flow screens (BR-99). Pages slot their
 * form content inside; descendant styles below are scoped under the unique
 * `.luna-auth-card` root, so they can style page-provided form parts
 * (labels, inputs, links) without leaking to other pages.
 */
@customElement('luna-auth-card')
export class LunaAuthCard extends LitPage {
  content() {
    return html`
      <div class="luna-auth-card">
        <div class="luna-auth-card__panel">
          <slot></slot>
        </div>
      </div>
    `;
  }

  styles() {
    return css`
      .luna-auth-card {
        display: flex;
        justify-content: center;
        padding: 56px 16px 24px;
        box-sizing: border-box;
      }

      .luna-auth-card__panel {
        width: 100%;
        max-width: 420px;
        background: #ffffff; /* derived: auth panel on themed page bg */
        border-radius: var(--luna-radius-card-sm);
        box-shadow:
          0 2px 6px rgba(16, 24, 40, 0.12),
          0 10px 24px rgba(16, 24, 40, 0.1);
        padding: 28px 26px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        box-sizing: border-box;
        font-family: var(--mbw-font-sans);
        color: var(--mbw-text-ink);
      }

      /* Scoped descendants — page-provided auth form parts (BR-99). */
      .luna-auth-card h1,
      .luna-auth-card p {
        margin: 0;
      }

      .luna-auth-card p {
        color: var(--mbw-text-muted);
      }

      .luna-auth-card form {
        display: flex;
        flex-direction: column;
        gap: 14px;
      }

      .luna-auth-card label {
        display: block;
        font-weight: 600;
        font-size: 14px;
        color: var(--mbw-text-ink);
      }

      .luna-auth-card input {
        width: 100%;
        min-height: 48px;
        margin-top: 6px;
        padding: 0 14px;
        box-sizing: border-box;
        border: 2px solid #b9c2cc;
        border-radius: var(--luna-radius-btn-sm);
        font-size: 16px;
        font-family: var(--mbw-font-sans);
        color: var(--mbw-text-ink);
        background: #ffffff;
      }

      .luna-auth-card input:focus-visible {
        outline: 2px solid var(--cds-focus);
        outline-offset: 1px;
        border-color: var(--cds-focus);
      }

      .luna-auth-card .auth-error {
        color: #c8392b;
        font-weight: 600;
      }

      .luna-auth-card .auth-notice {
        color: #2f7a3d;
        font-weight: 600;
      }

      /* BR-76: error/notice render stably and toggle via hidden — never
         conditionally rendered inside the slot. */
      .luna-auth-card .auth-error[hidden],
      .luna-auth-card .auth-notice[hidden] {
        display: none;
      }

      /* Visually-hidden native submit so Enter-key form submission keeps
         working alongside the visible luna-button (type="button"). */
      .luna-auth-card .auth-submit-ghost {
        position: absolute;
        width: 1px;
        height: 1px;
        margin: -1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        white-space: nowrap;
        border: 0;
      }

      .luna-auth-card .auth-links {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
      }

      .luna-auth-card .auth-link {
        border: none;
        background: none;
        padding: 0;
        min-height: 44px; /* NFR-1 */
        color: #0f62fe;
        font-size: 15px;
        font-weight: 600;
        font-family: var(--mbw-font-sans);
        text-decoration: underline;
        cursor: pointer;
      }

      .luna-auth-card .auth-link:focus-visible {
        outline: 2px solid var(--cds-focus);
        outline-offset: 2px;
      }
    `;
  }
}
